import { useContext, useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { ROUTES, KOT_MESSAGES, KEYBOARD_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import styles from "./styles.module.css";
import CartItem from "./CartItem";

import SalesContext from "../../../context/SalesContext";
import ShopContext from "context/ShopContext";
import ProductContext from "context/ProductContext";
import SalesPersonContext from "context/SalesPersonContext";

import PrinterImg from "../../../images/svg/printer.svg";
import KotImg from "../../../images/svg/house-2.svg";

import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import ShopsHelper from "helpers/ShopsHelper";
import KotManager from "helpers/KotManager";
import KOTInvoice from "../PrintInvoice/KOTInvoice";
import * as TerminalHelper from "helpers/TermialHelper";
import * as SalesHelper from "helpers/SalesHelper";
import { isScanningBarcode } from "helpers/BarcodeHelper";

import { ReactComponent as MenuIcon } from "images/svg/menu.svg";
import { ReactComponent as DiscountIcon } from "images/svg/discount.svg";
import { ReactComponent as CardIcon } from "images/svg/card.svg";
import { ReactComponent as SalespersonIcon } from "images/svg/Salespersons.svg";
import RemarksImg from "images/svg/icons_remark.png";
import CustomerSelector from "components/utils/CustomerSelector/CustomerSelector";
import PersonalCart from "images/svg/personalcard.svg";

function Cart({ cartItemEdit, invoiceEditHandler, onItemClick }) {
  const cartRef = useRef(null);
  const {
    activeSale,
    handleSaveDraftSale,
    handleClearActiveSale,
    handleChangePriceCategory,
    selectedPriceCategory,
    chargeGroupsAppliedForSale,
    addChargeGroupToActiveSale,
    handleChangeSelectedCustomer,
    handleSetSalesPerson,
    setActiveSale,
  } = useContext(SalesContext);

  const handleSaveRemarks = async () => {
    let updatedSale = { ...activeSale, remark: tempRemarks };
    if (!updatedSale.editing_sale_local_id) {
      const saleLocalId = await SalesHelper.saveSaleInDB(updatedSale);
      updatedSale = { ...updatedSale, local_id: saleLocalId };
      eventsEmitter.emit(EVENTS.SALES_UPDATED);
    } else {
      if (activeSale.remark && tempRemarks) {
        updatedSale.remark = `${activeSale.remark}, ${tempRemarks}`;
      } else {
        updatedSale.remark = tempRemarks || activeSale.remark || "";
      }
    }
    setActiveSale(updatedSale);
    setOpenRemarksDialog(false);
  };

  const navigate = useNavigate();

  const { selectedShop, hasKotValidity } = useContext(ShopContext);
  const { categories, priceCategories, chargeGroups } = useContext(ProductContext);
  const { salesPersons } = useContext(SalesPersonContext);

  const kotPrintRef = useRef(null);

  const [printData, setPrintData] = useState(null);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [anchorMoreEl, setAnchorMoreEl] = useState(null);
  const [anchorCategoryEl, setAnchorCategoryEl] = useState(null);
  const [anchorChargeEl, setAnchorChargeEl] = useState(null);
  const [anchorSalesPersonEl, setAnchorSalesPersonEl] = useState(null);
  const [isProcessingKOT, setIsProcessingKOT] = useState(false);
  const [openRemarksDialog, setOpenRemarksDialog] = useState(false);
  const [tempRemarks, setTempRemarks] = useState(activeSale?.remark || "");

  useEffect(() => {
    setTempRemarks(activeSale?.remark || "");
  }, [activeSale?.remark]);

  const openMoreMenu = Boolean(anchorMoreEl);
  const openCategoryMenu = Boolean(anchorCategoryEl);
  const openChargeMenu = Boolean(anchorChargeEl);
  const openSalesPersonMenu = Boolean(anchorSalesPersonEl);

  const onMoreClick = (event) => {
    setAnchorMoreEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const onPriceCategoryClick = (event) => {
    setAnchorCategoryEl(event.currentTarget);
  };

  const onChargeGroupClick = (event) => {
    setAnchorChargeEl(event.currentTarget);
  };
  const onSalesPersonClick = (event) => {
    setAnchorSalesPersonEl(event.currentTarget);
  };

  const handleWaiterSelect = async (salesPerson) => {
    await handleSetSalesPerson(salesPerson);
    setAnchorSalesPersonEl(null);
    setAnchorMoreEl(null);
  };

  const isPremium = ShopsHelper.isPremiumShop(selectedShop);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: kotPrintRef,
    documentTitle: `KOT-${activeSale?.bill_no || "NA"}`,
  });

  const printAndUpdateKot = useCallback(
    async (sale) => {
      try {
        const result = await KotManager.processKOT({
          activeSale: sale,
          categories,
          fallbackPrintFunction: handleWebInvoicePrint,
        });

        if (!result.success) {
          if (result.message === KOT_MESSAGES.FAILED) toast.error(result.message);
          return;
        }

        if (result.message === KOT_MESSAGES.NO_CHANGES) {
          toast.error(result.message);
          return;
        }

        setPrintData(result.saleToPrint);

        await handleSaveDraftSale(
          {
            ...sale,
            items: result.updatedItems,
            token_no: result.token_no,
            kot_status: result.kot_status,
            deletedItems: [],
          },
          true,
        );
      } catch (err) {
        console.error("KOT Error:", err);
        toast.error("Failed to process KOT.");
      } finally {
        setPrintData(null);
        setIsProcessingKOT(false);
      }
    },
    [categories, handleWebInvoicePrint, handleSaveDraftSale],
  );

  const onClickKotButton = useCallback(async () => {
    if (isProcessingKOT) return;
    if (!activeSale) return;

    setIsProcessingKOT(true);

    // Intentionally not awaiting to finish the KOT printing
    printAndUpdateKot(activeSale);

    const terminalSettings = TerminalHelper.getTerminalSettings();
    const isTableDashboardEnabled = terminalSettings?.isTableDashboardEnabled;
    const isActiveOrderEnabled = terminalSettings?.isOrderDashboardEnabled;

    if (isTableDashboardEnabled) {
      navigate(ROUTES.TABLE_DASHBOARD, { state: { refresh: true } });
    } else if (isActiveOrderEnabled) {
      navigate(ROUTES.ACTIVE_ORDERS, { state: { refresh: true } });
    } else {
      await handleClearActiveSale();
    }
    setIsProcessingKOT(false);
  }, [isProcessingKOT, activeSale, printAndUpdateKot, navigate, handleClearActiveSale]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA") {
        return;
      }
      if (isScanningBarcode() || event.defaultPrevented || event.isHandled) {
        return;
      }
      if (event.key === KEYBOARD_KEYS.F3) {
        event.preventDefault();
        onClickKotButton();
      } else if (event.key === KEYBOARD_KEYS.F4 || event.key === KEYBOARD_KEYS.ENTER) {
        event.preventDefault();
        if (!(activeSale?.items?.length || 0)) {
          toast.error("Empty cart");
          return;
        }
        invoiceEditHandler();
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [onClickKotButton, invoiceEditHandler, activeSale?.items?.length]);

  useEffect(() => {
    if (cartRef.current) {
      cartRef.current.scrollTop = cartRef.current.scrollHeight;
    }
  }, [activeSale?.items?.length]);

  return (
    <div className={styles.CartWrapper}>
      <div className={styles.customerSection}>
        {activeSale?.customer ? (
          <div className={styles.customerSelectedBtn}>
            <div className={styles.customerInfo}>
              <span className={styles.customerName}>{activeSale.customer.name}</span>
              {activeSale.customer.mobile && (
                <span className={styles.customerMobile}>{activeSale.customer.mobile}</span>
              )}
            </div>
            <div className={styles.customerBtn} onClick={() => handleChangeSelectedCustomer(null)}>
              <img src={PersonalCart} alt="customer" className={styles.paymentIcon} />
              <h3>Remove</h3>
            </div>
          </div>
        ) : (
          <div className={styles.customerBtn} onClick={() => setShowCustomerSelector((prev) => !prev)}>
            <img src={PersonalCart} alt="customer" className={styles.paymentIcon} />
            <h3>Add Customer</h3>
          </div>
        )}

        {showCustomerSelector && (
          <CustomerSelector
            open={showCustomerSelector}
            onClose={() => setShowCustomerSelector(false)}
            onSelect={(customer) => {
              handleChangeSelectedCustomer(customer);
              setShowCustomerSelector(false);
            }}
            style={{
              position: "absolute",
              top: "100%",
              bottom: "unset",
              left: "10px",
              right: "10px",
              width: "auto",
              zIndex: 1000,
            }}
          />
        )}
      </div>
      <div ref={cartRef} className={styles.cartItemList}>
        <Grid container>
          {activeSale?.items?.length ? (
            activeSale.items.map((saleItem, index) => {
              return (
                <CartItem
                  key={String(saleItem.localId)}
                  index={index}
                  saleItem={saleItem}
                  editItem={cartItemEdit}
                  onCartItemClick={onItemClick}
                />
              );
            })
          ) : (
            <Grid item xs={12} p={2} display="flex" justifyContent="center">
              <span className={styles.description}>Active cart is empty.</span>
            </Grid>
          )}
        </Grid>
      </div>

      {/* SUMMARY */}
      <div className={styles.totalWrapper}>
        <Grid container className={styles.priceContainer}>
          {/* Row 1: Total | Tax */}
          <Grid item xs={12} className={styles.amountSection}>
            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Total ({activeSale?.items?.length || 0})</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>{NumberFormatHelper.roundOff(activeSale?.total || 0)}</h5>
              </div>
            </Grid>

            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Tax</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>{NumberFormatHelper.roundOff(activeSale.cess + activeSale.tax)}</h5>
              </div>
            </Grid>
          </Grid>

          {/* Row 2: Charge | Discount */}
          <Grid item xs={12} className={styles.amountSection}>
            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Charge</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>{NumberFormatHelper.roundOff(activeSale?.totalChargeAmount || 0)}</h5>
              </div>
            </Grid>

            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Discount</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>
                  {NumberFormatHelper.roundOff(
                    (activeSale?.discount || 0) + (activeSale?.discount_applied_on_items || 0),
                  )}
                </h5>
              </div>
            </Grid>
          </Grid>

          {/* Row 3: Cart | Round Off */}
          <Grid item xs={12} className={styles.amountSection}>
            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Cart</h5>
              <h5>{activeSale?.local_id || 0}</h5>
            </Grid>

            <Grid item xs={6} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Round Off</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>{NumberFormatHelper.roundOff(activeSale.roundOff || 0)}</h5>
              </div>
            </Grid>
          </Grid>

          {/* Row 4: Payable (Single Column) */}
          <Grid item xs={12} className={styles.amountSection}>
            <Grid item xs={12} className={styles.amountField}>
              <h5 style={{ color: "#8b8b8b" }}>Payable</h5>
              <div className={styles.amount}>
                <div className={styles.rupeesIcon}>{selectedShop?.currency_symbol}</div>
                <h5>{NumberFormatHelper.roundOff(activeSale?.payable || 0)}</h5>
              </div>
            </Grid>
          </Grid>
        </Grid>

        {/* BUTTONS */}
        <Grid item xs={9} className={styles.btnField}>
          {/* More Button */}
          <Button onClick={onMoreClick} className={styles.moreSmall}>
            <MenuIcon className={styles.icon} />
          </Button>

          {/* More Dropdown Menu */}
          <Menu
            anchorEl={anchorMoreEl}
            open={openMoreMenu}
            onClose={handleMoreClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            disablePortal={false}
            disableAutoFocusItem={true}
            slotProps={{
              paper: {
                className: styles.moreMenuPaper,
              },
            }}
          >
            <MenuItem
              key="sales-staff-item"
              onClick={(e) => {
                onSalesPersonClick(e);
              }}
            >
              <SalespersonIcon className={styles.icon} />
              <div className={styles.menuItemContent}>
                <span>Sales Staff</span>
                <span className={styles.menuItemValueTruncated}>{activeSale?.waiter?.name || "None"}</span>
              </div>
            </MenuItem>
            {isPremium && (
              <>
                <MenuItem
                  key="price-category-item"
                  onClick={(e) => {
                    onPriceCategoryClick(e);
                  }}
                >
                  <DiscountIcon className={styles.icon} />
                  <div className={styles.menuItemContent}>
                    <span>Price Category</span>
                    <span className={styles.menuItemValue}>
                      {SalesHelper.getActiveSalePriceCategory(activeSale, selectedPriceCategory)?.name || "Default"}
                    </span>
                  </div>
                </MenuItem>
                <MenuItem
                  key="charge-group-item"
                  onClick={(e) => {
                    onChargeGroupClick(e);
                  }}
                >
                  <CardIcon className={styles.icon} />
                  <div className={styles.menuItemContent}>
                    <span>Charge Groups</span>
                    <span className={styles.menuItemValueTruncated}>
                      {chargeGroupsAppliedForSale?.length > 0
                        ? chargeGroupsAppliedForSale.map((g) => g.name).join(", ")
                        : "Default"}
                    </span>
                  </div>
                </MenuItem>
              </>
            )}
            <MenuItem
              key="remarks-item"
              onClick={() => {
                setAnchorMoreEl(null);
                setOpenRemarksDialog(true);
              }}
            >
              <img src={RemarksImg} alt="Remarks" className={`${styles.icon} ${styles.remarksIcon}`} />
              <div className={styles.menuItemContent}>
                <span>Remarks</span>
                <span className={styles.menuItemValueTruncated}>{activeSale?.remark ? "Added" : "None"}</span>
              </div>
            </MenuItem>
          </Menu>

          {/* Price Category Menu */}
          <Menu
            anchorEl={anchorCategoryEl}
            open={openCategoryMenu}
            onClose={() => setAnchorCategoryEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            disablePortal={false}
            disableAutoFocusItem={true}
            slotProps={{
              paper: {
                className: styles.popupMenuPaper,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleChangePriceCategory(null);
                setAnchorCategoryEl(null);
                setAnchorMoreEl(null);
              }}
            >
              Default
            </MenuItem>
            {priceCategories.map((priceCategorie) => (
              <MenuItem
                key={priceCategorie.id}
                onClick={() => {
                  handleChangePriceCategory(priceCategorie);
                  setAnchorCategoryEl(null);
                  setAnchorMoreEl(null);
                }}
              >
                {priceCategorie.name}
              </MenuItem>
            ))}
          </Menu>

          {/* Charge Groups Menu */}
          <Menu
            anchorEl={anchorChargeEl}
            open={openChargeMenu}
            onClose={() => setAnchorChargeEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            disablePortal={false}
            disableAutoFocusItem={true}
            slotProps={{
              paper: {
                className: styles.popupMenuPaper,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                addChargeGroupToActiveSale(null);
                setAnchorChargeEl(null);
                setAnchorMoreEl(null);
              }}
            >
              Default
            </MenuItem>
            {chargeGroups.map((chargeGroup) => {
              const isSelected = chargeGroupsAppliedForSale.some(
                (selectedChargeGroup) => selectedChargeGroup.id === chargeGroup.id,
              );
              return (
                <MenuItem
                  key={chargeGroup.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    addChargeGroupToActiveSale(chargeGroup);
                  }}
                >
                  <Checkbox checked={isSelected} size="small" sx={{ p: 0, mr: 1 }} />
                  {chargeGroup.name}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Sales Staff Menu */}
          <Menu
            anchorEl={anchorSalesPersonEl}
            open={openSalesPersonMenu}
            onClose={() => setAnchorSalesPersonEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            disablePortal={false}
            disableAutoFocusItem={true}
            slotProps={{
              paper: {
                className: styles.popupMenuPaper,
              },
            }}
          >
            <MenuItem onClick={() => handleWaiterSelect(null)}>Default</MenuItem>
            {salesPersons.map((salesPerson) => (
              <MenuItem key={salesPerson.id} onClick={() => handleWaiterSelect(salesPerson)}>
                {salesPerson.name}
              </MenuItem>
            ))}
          </Menu>

          {hasKotValidity && (
            <Button
              fullWidth
              className={styles.billBtn}
              onClick={onClickKotButton}
              disabled={isProcessingKOT}
              startIcon={<img src={KotImg} alt="KOT" className={styles.iconSmall} />}
            >
              {isProcessingKOT ? "Processing..." : "KOT(F3)"}
            </Button>
          )}

          <Button
            fullWidth
            className={styles.billBtn}
            onClick={() => {
              if (!(activeSale?.items?.length || 0)) {
                toast.error("Empty cart");
                return;
              }
              invoiceEditHandler();
            }}
            startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
          >
            Invoice(F4)
          </Button>
        </Grid>
      </div>

      {/* Remarks Dialog */}
      <Dialog
        open={openRemarksDialog}
        onClose={() => setOpenRemarksDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "20px",
            mb: 1,
            p: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Sale Remarks</span>
          <IconButton onClick={() => setOpenRemarksDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TextField
            autoFocus
            margin="dense"
            id="remarks"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={tempRemarks}
            onChange={(e) => setTempRemarks(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === KEYBOARD_KEYS.ENTER && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                handleSaveRemarks();
              }
            }}
            placeholder="Enter any notes or remarks for this sale..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": {
                  border: "1px dashed #aaa",
                },
                "&:hover fieldset": {
                  border: "1px dashed #aaa",
                },
                "&.Mui-focused fieldset": {
                  border: "1px dashed #aaa",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 0, mt: 3, justifyContent: "flex-end" }}>
          <Button
            onClick={handleSaveRemarks}
            variant="contained"
            sx={{
              backgroundColor: "#e50218",
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#c00214" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* HIDDEN PRINT */}
      <div style={{ display: "none" }}>
        <KOTInvoice ref={kotPrintRef} sale={printData} />
      </div>
    </div>
  );
}

export default Cart;
